import React, { useState, useEffect } from 'react';
import { FaTimes, FaMapMarkerAlt, FaTrash } from 'react-icons/fa';
import HeartIcon from '../Icons/HeartIcon';
import { getFavorites, removeFromFavorites } from '../../services/api';
import { getTranslation } from '../../utils/translations';
import { useLanguage } from '../../App';
import ResponsiveImage from '../ResponsiveImage';
import './FavoritesList.css';

const FavoritesList = ({ isVisible, onClose, onNavigateToUnit }) => {
  const { language } = useLanguage();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isVisible) {
      loadFavorites();
    }
  }, [isVisible]);

  const loadFavorites = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getFavorites();
      setFavorites(response.data.data || []);
    } catch (err) {
      console.error('Error loading favorites:', err);
      setError(language === 'ar' ? 'حدث خطأ في تحميل المفضلة' : 'Error loading favorites');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFavorite = async (parcelId) => {
    try {
      await removeFromFavorites(parcelId);
      setFavorites(favorites.filter(fav => fav.id !== parcelId));
    } catch (err) {
      console.error('Error removing favorite:', err);
      alert(language === 'ar' ? 'حدث خطأ في حذف المفضلة' : 'Error removing favorite');
    }
  };

  const handleNavigateToUnit = (parcel) => {
    if (onNavigateToUnit) {
      onNavigateToUnit(parcel);
    }
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "#10b981";
      case "not available":
        return "#3b82f6";
      case "reserved":
        return "#f59e0b";
      case "sold":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return language === "ar" ? "متاح" : "Available";
      case "not available":
        return language === "ar" ? "غير متاح" : "Not Available";
      case "reserved":
        return language === "ar" ? "محجوز" : "Reserved";
      case "sold":
        return language === "ar" ? "مباع" : "Sold";
      default:
        return status;
    }
  };

  const formatPrice = (price) => {
    if (!price) return language === 'ar' ? 'غير محدد' : 'Not specified';
    return new Intl.NumberFormat('en-US').format(price);
  };

  if (!isVisible) return null;

  return (
    <div className="favorites-overlay">
      <div className={`favorites-modal ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="favorites-header">
          <h2>
            <HeartIcon filled={true} size={18} color="#dc2626" className="favorites-icon" />
            {language === 'ar' ? 'المفضلة' : 'Favorites'}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="favorites-content">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadFavorites} className="retry-btn">
                {language === 'ar' ? 'إعادة المحاولة' : 'Retry'}
              </button>
            </div>
          ) : favorites.length === 0 ? (
            <div className="empty-state">
              <HeartIcon filled={false} size={48} color="#d1d5db" className="empty-icon" />
              <h3>{language === 'ar' ? 'لا توجد وحدات مفضلة' : 'No favorite units'}</h3>
              <p>{language === 'ar' ? 'ابدأ بإضافة وحدات للمفضلة لتظهر هنا' : 'Start adding units to favorites to see them here'}</p>
            </div>
          ) : (
            <div className="favorites-list">
              {favorites.map((parcel) => (
                <div key={parcel.id} className="favorite-item">
                  <div className="favorite-image">
                    <ResponsiveImage
                      desktopImage={parcel.unit_image_desktop_url}
                      mobileImage={parcel.unit_image_mobile_url}
                      fallbackImage={parcel.default_unit_image_url}
                      alt={`${getTranslation("parcel", language)} ${parcel.parcel_number}`}
                      className="unit-image"
                      layout="responsive"
                      aspectRatio="16:9"
                      language={language}
                    />
                  </div>
                  
                  <div className="favorite-details">
                    <div className="favorite-header">
                      <h3>
                        {getTranslation("parcel", language)} {parcel.parcel_number}
                      </h3>
                      <div
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(parcel.sales_status) }}
                      >
                        {getStatusText(parcel.sales_status)}
                      </div>
                    </div>
                    
                    <div className="favorite-info">
                      <p className="project-name">
                        {parcel.block?.project?.name || 'Unknown Project'}
                      </p>
                      <p className="area">
                        {getTranslation("area", language)}: {parcel.area} m²
                      </p>
                      {parcel.price_with_tax && (
                        <p className="price">
                          {formatPrice(parcel.price_with_tax)} {language === 'ar' ? 'ر.س' : 'SAR'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="favorite-actions">
                    <button
                      className="navigate-btn"
                      onClick={() => handleNavigateToUnit(parcel)}
                      title={language === 'ar' ? 'الانتقال للوحدة' : 'Navigate to unit'}
                    >
                      <FaMapMarkerAlt />
                    </button>
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveFavorite(parcel.id)}
                      title={language === 'ar' ? 'حذف من المفضلة' : 'Remove from favorites'}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FavoritesList;
